<script lang="ts">
	import type { FooterLinks } from '$lib/components/seo/types';
	import type { PoiCategory } from '../types';
	import { browser } from '$app/environment';
	import { GetPoiFiltersStore } from '$houdini';
	import Footer from '$lib/components/footer/Footer.svelte';
	import Header from '$lib/components/header/Header.svelte';
	import { track } from '$lib/services/tracking/googleAnalytics';
	import { getPoiCategoryToggleTrackingEvent } from '$lib/services/tracking/googleAnalytics/pois';
	import * as m from '$paraglide/messages';
	import { createEventDispatcher } from 'svelte';
	import DetailedViewSwitch from '../list/DetailedViewSwitch.svelte';
	import { areCategoriesWithMichelinPois } from '../list/utils';
	import MichelinSwitchFilter from './components/MichelinSwitchFilter.svelte';
	import CorridorFilterCard from './CorridorFilterCard.svelte';
	import FilterCard from './FilterCard.svelte';
	import FiltersError from './FiltersError.svelte';
	import PoiCategories from './PoiCategories.svelte';

	export let selectedPoiCategories: PoiCategory[];
	export let forCorridor = false;
	export let withPartner = false;
	export let detailedView = false;
	export let onPoiCategoriesChange: (categories: PoiCategory[]) => void = () => {};

	let extraClasses = '';
	export { extraClasses as class };

	// TODO: remove this when we have a better way to handle footer links
	export let partnersLinks: FooterLinks[];

	const dispatch = createEventDispatcher<{
		michelinSelectionChange: boolean;
		poiFilterChange: void;
		goBack: void;
	}>();

	const CATEGORIES_ORDER: Record<PoiCategory, number> = {
		ACCOMMODATION: 0,
		RESTAURANT: 1,
		TOURISM: 2,
		SERVICE_GAS_STATION: 3,
		EV_SERVICE: 4,
		PARKING: 5,
		REST_STOP: 6,
		PARTNER: 7,
		PARTNER2: 8,
	};

	$: sortedCategories = [...selectedPoiCategories]?.sort((catA, catB) => {
		return CATEGORIES_ORDER[catA] - CATEGORIES_ORDER[catB];
	});

	const queryStore = new GetPoiFiltersStore();

	$: isGetPoiFiltersError = !!$queryStore?.errors;

	function fetchPoiFilters() {
		queryStore.fetch().catch(() => {
			isGetPoiFiltersError = true;
		});
	};

	if (browser) {
		fetchPoiFilters();
	}
</script>

<div class="flex flex-col h-full md:gap-2 {extraClasses}">
	<Header
		hasRoleBanner
		variant="no-logo"
		leftButton={{ icon: 'west', text: m.poi_filters__back() }}
		onLeftButtonClick={() => {
			dispatch('goBack');
		}}
	/>

	<div class="flex flex-col gap-4 p-2 pb-0 md:pt-0 lt-sm:overflow-y-scroll">
		{#if !forCorridor}
			<div class="flex items-center min-h-11">
				<PoiCategories
					{selectedPoiCategories}
					{withPartner}
					onChange={(categories, info) => {
						onPoiCategoriesChange(categories);
						track(getPoiCategoryToggleTrackingEvent({ ...info, updatedSelectedCategories: categories, context: 'advancedFilters' }));
					}}
				/>
			</div>
		{/if}

		{#if areCategoriesWithMichelinPois(selectedPoiCategories)}
			<MichelinSwitchFilter
				pageContext="poi.filter"
				class="shadow-smooth-block"
				onchange={isOnlyMichelin => dispatch('michelinSelectionChange', isOnlyMichelin)}
			/>
		{/if}

		{#if forCorridor}
			<CorridorFilterCard on:corridorRadiusChange />
		{/if}

		<div class="bg-neutral-weakest p-2 rd-2 shadow-smooth-block" data-testid="detailed-view-switch">
			<DetailedViewSwitch {detailedView} />
		</div>

		{#if sortedCategories.length === 0}
			<FiltersError errorType="empty" />
		{/if}

		{#if isGetPoiFiltersError}
			<FiltersError errorType="fetch" onRetry={() => fetchPoiFilters()} />
		{:else}
			{#each sortedCategories as category (category)}
				{#if category !== 'PARTNER' && category !== 'PARTNER2'}
					<FilterCard
						poiCategory={category}
						isLoading={$queryStore?.fetching}
						filters={$queryStore?.data?.getPoiFilters?.filtersV2}
						currency={$queryStore?.data?.getPoiFilters?.currency}
						on:selectDates
						on:clearDates
						on:change={() => {
							dispatch('poiFilterChange');
						}}
					/>
				{/if}
			{/each}
		{/if}
		<!-- TODO: refactor how filters panel is rendered full height on mobile and move footer to the page itself -->
		<Footer {partnersLinks} />
	</div>
</div>
